import type {
  ProductColor,
  QualityLabel,
  RegressionOutputSuccess,
  SensitivityAnalysisResult,
  SensitivityFeatureKey,
  SensitivityRoundResult,
} from '@aloft/toolkit-shared';
import {
  AloftPrice,
  QUALITY_LABEL_DISPLAY,
  formatUnitRateForDisplay,
  getCompsWithVariationStatus,
  getRegressionRSquaredDescription,
  getRegressionSampleSizeDescription,
  getRegressionStandardErrorRatioDescription,
  getSensitivitySpreadRatioDescription,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Flex, Tooltip, Typography, colors } from '@aloft/toolkit-sui';

const qualityColor = (label: QualityLabel): ProductColor => {
  if (label === 'EXCELLENT' || label === 'GOOD') return 'textSuccess';
  if (label === 'ACCEPTABLE') return 'textSecondary';
  if (label === 'WEAK') return 'textWarning';
  return 'textError';
};
type StatTileProps = {
  label: string;
  value: string;
  info: string | null;
  quality?: QualityLabel;
  /**
   * Overrides the displayed status text. `quality` still drives the badge color.
   * Used by tiles whose states have their own semantic names (e.g. "Comps
   * with Variation" tile: "Not Bracketed") distinct from the generic
   * EXCELLENT/GOOD/ACCEPTABLE/WEAK/UNRELIABLE ladder.
   */
  qualityLabel?: string;
};

export const StatTile = ({ label, value, quality, qualityLabel, info }: StatTileProps) =>
  info ? (
    <Tooltip label={info} placement="top" hasArrow>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="0.5rem"
        padding="1rem"
        backgroundColor="surface1"
        border={`1px solid ${colors.strokeDefault}`}
        borderRadius="8px"
        minWidth="140px"
        height="100px"
        flexShrink={0}
        __dangerDoNotUseSx={{ cursor: 'help' }}
      >
        <Typography variant="body2" color="textLight" textAlign="center">
          {label}
        </Typography>
        <Typography variant="h2">{value}</Typography>
        {quality && (
          <Typography variant="subtitle2" color={qualityColor(quality)}>
            {qualityLabel ?? QUALITY_LABEL_DISPLAY[quality]}
          </Typography>
        )}
      </Flex>
    </Tooltip>
  ) : (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="0.5rem"
      padding="1rem"
      backgroundColor="surface1"
      border={`1px solid ${colors.strokeDefault}`}
      borderRadius="8px"
      minWidth="140px"
      height="100px"
      flexShrink={0}
    >
      <Typography variant="body2" color="textLight" textAlign="center">
        {label}
      </Typography>
      <Typography variant="h2">{value}</Typography>
      {quality && (
        <Typography variant="subtitle2" color={qualityColor(quality)}>
          {qualityLabel ?? QUALITY_LABEL_DISPLAY[quality]}
        </Typography>
      )}
    </Flex>
  );

type StatsRowProps = {
  type: 'regression' | 'sensitivity';
  output?: RegressionOutputSuccess;
  result?: SensitivityAnalysisResult;
  featureKey?: SensitivityFeatureKey;
  round?: SensitivityRoundResult;
  isLatestRound?: boolean;
};

const getRegressionTiles = (output: RegressionOutputSuccess): StatTileProps[] => {
  const sampleSizeDescription = getRegressionSampleSizeDescription(output.sampleSize);
  const regressionRSquaredDescription = getRegressionRSquaredDescription(
    output.modelDiagnostics.rSquared,
  );
  const regressionStandardErrorRatioDescription = getRegressionStandardErrorRatioDescription(
    output.modelDiagnostics.standardErrorRatio,
  );
  const tiles: StatTileProps[] = [
    {
      label: 'Sample Size',
      value: String(output.sampleSize),
      quality: sampleSizeDescription.label,
      info: sampleSizeDescription.warning,
    },
    { label: 'Variables', value: String(output.numberOfVariables), info: null },
    {
      label: 'Constant',
      value: AloftPrice.schema.parse(output.constant).toString(true),
      info: null,
    },
    {
      label: 'R-squared',
      value: `${(output.modelDiagnostics.rSquared * 100).toFixed(0)}%`,
      quality: regressionRSquaredDescription.label,
      info: regressionRSquaredDescription.warning,
    },
    {
      label: 'Standard Error',
      value: `${(output.modelDiagnostics.standardErrorRatio * 100).toFixed(0)}%`,
      quality: regressionStandardErrorRatioDescription.label,
      info: regressionStandardErrorRatioDescription.warning,
    },
  ];
  if (output.modelDiagnostics.crossValidatedStandardErrorRatio !== undefined) {
    const description = getRegressionStandardErrorRatioDescription(
      output.modelDiagnostics.crossValidatedStandardErrorRatio,
    );
    tiles.push({
      label: 'Out-of-sample Error',
      value: `${(output.modelDiagnostics.crossValidatedStandardErrorRatio * 100).toFixed(0)}%`,
      quality: description.label,
      info: 'Average sale-price prediction error across five held-out transaction folds.',
    });
  }
  return tiles;
};

const getSensitivityTiles = (
  result: SensitivityAnalysisResult,
  featureKey: SensitivityFeatureKey,
  round?: SensitivityRoundResult,
  isLatestRound?: boolean,
): StatTileProps[] => {
  // Iterations dropdown: when a non-latest round is selected, re-color the tiles using
  // that round's summary rather than the final result (the chart/table always show the
  // final/latest result — see SensitivityAnalysisModal.tsx for why).
  if (round) {
    const compsWithVariationStatus = getCompsWithVariationStatus({
      bracketStatus: round.bracketStatus,
    });

    const spreadRatioDescription =
      round.magnitudeStatus === 'unreliable'
        ? {
            warning:
              'Within-group price spread exceeds between-group price spread for this feature; any unit rate produced may not achieve sufficient signal strength to support a market-derived adjustment.',
            label: 'UNRELIABLE' as const,
          }
        : getSensitivitySpreadRatioDescription(round.percentVariance, featureKey);

    const optimalRate = orUndefined(AloftPrice.fromDollarLike)(round.optimalRate);

    return [
      {
        label: 'Optimal Unit Rate',
        value: optimalRate ? formatUnitRateForDisplay(optimalRate, featureKey) : '-',
        info: isLatestRound && !round.converged ? 'This round did not converge.' : null,
      },
      {
        label: 'Percent Variance',
        value: `${(round.percentVariance * 100).toFixed(0)}%`,
        quality: spreadRatioDescription.label,
        info: spreadRatioDescription.warning,
      },
      {
        label: 'Comps with Variation',
        value: String(round.compsWithVariation),
        quality: compsWithVariationStatus.quality,
        qualityLabel: compsWithVariationStatus.label,
        info: null,
      },
    ];
  }

  const compsWithVariationStatus = getCompsWithVariationStatus(result);
  const compsWithVariationTile: StatTileProps = {
    label: 'Comps with Variation',
    value: String(result.nonAnchoredCompCount ?? 0),
    quality: compsWithVariationStatus.quality,
    qualityLabel: compsWithVariationStatus.label,
    info: null,
  };

  // When analysis couldn't run due to insufficient variation, only show the count tile
  if (result.insufficientVariation) return [compsWithVariationTile];

  const parsedOptimalRate = orUndefined(AloftPrice.schema.parse)(result.selectedRate.rate);
  const optimalRate = parsedOptimalRate
    ? formatUnitRateForDisplay(parsedOptimalRate, featureKey)
    : '-';

  const { spreadRatio } = result.selectedRate;
  // The magnitude check is a separate axis from the spread-ratio ladder — if it flags
  // 'unreliable', that overrides the tier this tile would otherwise show.
  const spreadRatioDescription =
    result.magnitudeStatus === 'unreliable'
      ? {
          warning:
            'Within-group price spread exceeds between-group price spread for this feature; any unit rate produced may not achieve sufficient signal strength to support a market-derived adjustment.',
          label: 'UNRELIABLE' as const,
        }
      : getSensitivitySpreadRatioDescription(spreadRatio, featureKey);

  return [
    { label: 'Optimal Unit Rate', value: optimalRate, info: null },
    {
      label: 'Percent Variance',
      value: `${(spreadRatio * 100).toFixed(0)}%`,
      quality: spreadRatioDescription.label,
      info: spreadRatioDescription.warning,
    },
    compsWithVariationTile,
  ];
};

export const StatsRow = ({
  type,
  output,
  result,
  featureKey,
  round,
  isLatestRound,
}: StatsRowProps) => {
  const tiles =
    type === 'regression'
      ? getRegressionTiles(output!)
      : getSensitivityTiles(result!, featureKey ?? 'gla', round, isLatestRound);

  if (tiles.length === 0) return null;

  return (
    <Flex gap="1.5rem" justifyContent="center" flexWrap="wrap">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </Flex>
  );
};
